import { msToTime, pvCountToString } from 'core/util/default'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { getConfig, getPartnerId } from 'core/singleton'
import { IconVisibility } from 'components/Icons'
import { useTranslation } from 'react-i18next'

export interface CardHeaderOverlayProps {
  customBadgeInfo?: CollectConfig
  isLive?: boolean
  playTime?: string
  incomingCounterInfo?: number
}

const CardHeaderOverlay: React.FC<CardHeaderOverlayProps> = ({
  customBadgeInfo,
  incomingCounterInfo,
  isLive,
  playTime,
}) => {
  const partnerId = getPartnerId()

  const { t } = useTranslation()

  // 특정 파트너 사에서 z-index 값을 하향 조정 해달라는 이슈 대응
  const isLowerZIndex = useMemo(() => {
    return partnerId === 'ldfs'
  }, [partnerId])

  const liveWording = useMemo(() => {
    const { liveWording } = getConfig()
    return liveWording
  }, [])

  const customBadgeEl = useMemo(() => {
    if (!customBadgeInfo) return null

    const { backgroundColor, borderColor, collectName, fontColor } = customBadgeInfo
    return (
      <StyledCustomBadge
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        fontColor={fontColor}
      >
        {collectName}
      </StyledCustomBadge>
    )
  }, [customBadgeInfo])

  const headerEl = useMemo(() => {
    if (isLive) {
      return (
        <>
          <div className="badge-live">
            <span>{liveWording}</span>
          </div>
          <div className="badge-like">
            <IconVisibility />
            <div>
              {incomingCounterInfo ? pvCountToString(incomingCounterInfo) : incomingCounterInfo}
            </div>
          </div>
        </>
      )
    } else {
      return (
        <>
          <div className="badge-like">
            <IconVisibility />
            <div>
              <span className="blind_sf">{t('like')}</span>
              {incomingCounterInfo ? pvCountToString(incomingCounterInfo) : incomingCounterInfo}
            </div>
          </div>
          {customBadgeEl ? (
            customBadgeEl
          ) : (
            <div className="play_time">
              <span className="blind_sf">{t('vide_length')}</span>
              {msToTime(playTime)}
            </div>
          )}
        </>
      )
    }
  }, [customBadgeEl, isLive, liveWording, incomingCounterInfo, t, playTime])

  return <StyledHeader isLowerZIndex={isLowerZIndex}>{headerEl}</StyledHeader>
}

export default CardHeaderOverlay

const StyledHeader = styled.div<{ isLowerZIndex: boolean }>`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: ${({ isLowerZIndex }) => (isLowerZIndex ? 7 : 30)};
  .blind_sf {
    position: absolute;
    overflow: hidden;
    clip: rect(0 0 0 0);
    width: 1px;
    height: 1px;
    margin: -1px;
    line-height: normal;
    font-size: medium;
    white-space: nowrap;
  }
  .play_time {
    font-size: 12px;
    font-weight: 500;
    margin-left: auto;
    color: #ffffff;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2), 1px 1px 2px rgba(0, 0, 0, 0.2);
  }

  .badge-custom {
  }

  .badge-live {
    height: 24px;
    padding: 0 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background-color: ${(props) => props.theme.badge};
    margin-right: 4px;

    > span {
      font-size: 12px;
      font-weight: bold;
      line-height: 12px;
      color: #ffffff;
      text-shadow: 0 1px 1px rgba(0, 0, 0, 0.24);
    }
  }

  .badge-like {
    padding: 0 12px 0 10px;
    height: 24px;
    display: flex;
    align-items: center;
    border-radius: 12px;
    background-color: rgba(0, 0, 0, 0.4);

    div {
      margin-left: 4px;
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      color: #f8f8f9;
    }
  }
`

const StyledCustomBadge = styled.div<{
  backgroundColor: string
  borderColor: string
  fontColor: string
}>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0px 8px;
  border: 1px solid;
  border-radius: 10px;
  min-height: 24px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(props) => props.backgroundColor};
  border-color: ${(props) => props.borderColor};
  color: ${(props) => props.fontColor};
`
